<script lang="ts">
  import { onMount } from 'svelte';
  import { page, updated, navigating } from '$app/state';
  import { goto } from '$app/navigation';
  import { get } from 'svelte/store';
  import { wmsApp } from '$lib/wms/app/wms-app-state.svelte';
  import { canReloadWmsPage, wmsUpdateSafety } from '$lib/wms/app/wms-update-safety';
  import {
    isWmsPath, isWmsAppRouteAllowed, isWmsAppDisplay, shouldUseWmsApp,
    WMS_APP_SESSION_KEY, WMS_APP_START_PATH, WMS_APP_MANIFEST, WMS_APP_DISPLAY_QUERY, type WmsInstallPrompt
  } from '$lib/wms/app/wms-app';

  let mounted = $state(false);
  let visible = $state(true);
  let focused = $state(false);
  function resume() {
    visible = document.visibilityState !== 'hidden';
    focused = document.hasFocus();
    if (visible && isWmsPath(page.url.pathname)) void updated.check().catch(() => {});
  }
  onMount(() => {
    const display = window.matchMedia(WMS_APP_DISPLAY_QUERY);
    const syncDisplay = () => { wmsApp.inAppWindow = isWmsAppDisplay(); };
    syncDisplay();
    mounted = true;
    resume();
    const install = (event: Event) => {
      if (!isWmsPath(page.url.pathname)) return;
      event.preventDefault();
      wmsApp.installPrompt = event as WmsInstallPrompt;
    };
    const installed = () => { wmsApp.installPrompt = null; syncDisplay(); };
    window.addEventListener('beforeinstallprompt', install);
    window.addEventListener('appinstalled', installed);
    display.addEventListener('change', syncDisplay);
    document.addEventListener('fullscreenchange', syncDisplay);
    return () => {
      window.removeEventListener('beforeinstallprompt', install);
      window.removeEventListener('appinstalled', installed);
      display.removeEventListener('change', syncDisplay);
      document.removeEventListener('fullscreenchange', syncDisplay);
    };
  });
  $effect(() => {
    if (!mounted) return;
    let remembered = false;
    try { remembered = sessionStorage.getItem(WMS_APP_SESSION_KEY) === '1'; } catch {}
    const active = shouldUseWmsApp(page.url, wmsApp.inAppWindow, remembered);
    // Preserve the app shell while navigating through the shared login page.
    if (active) {
      wmsApp.active = true;
      try { sessionStorage.setItem(WMS_APP_SESSION_KEY, '1'); } catch {}
    }
    if (wmsApp.active && !isWmsAppRouteAllowed(page.url.pathname)) {
      void goto(WMS_APP_START_PATH, { replaceState: true });
    }
  });
  $effect(() => {
    if (!mounted || !updated.current || navigating.to || !visible || !focused
      || !canReloadWmsPage(page.url.pathname, $wmsUpdateSafety)) return;
    const timer = window.setTimeout(() => {
      if (document.visibilityState !== 'hidden' && document.hasFocus() && !navigating.to
        && canReloadWmsPage(page.url.pathname, get(wmsUpdateSafety))) window.location.reload();
    }, 1500);
    return () => window.clearTimeout(timer);
  });
</script>

<svelte:window onfocus={resume} onblur={() => { focused = false; }} onpageshow={resume} />
<svelte:document onvisibilitychange={resume} />
<svelte:head>
  {#if isWmsPath(page.url.pathname)}
    <link rel="manifest" href={WMS_APP_MANIFEST} />
    <meta name="theme-color" content="#0f172a" />
    <link rel="apple-touch-icon" href="/wms/icon-192.png" />
  {/if}
</svelte:head>
